import React, { useEffect, useRef, memo } from 'react';

// Map our coin symbols to TradingView symbols
const TV_SYMBOL: Record<string, string> = {
  BTC:  'BINANCE:BTCUSDT',
  ETH:  'BINANCE:ETHUSDT',
  XRP:  'BINANCE:XRPUSDT',
  SOL:  'BINANCE:SOLUSDT',
  BNB:  'BINANCE:BNBUSDT',
  ADA:  'BINANCE:ADAUSDT',
  DOGE: 'BINANCE:DOGEUSDT',
  DOT:  'BINANCE:DOTUSDT',
  USDT: 'BINANCE:USDTUSD',
};

interface TradingViewChartProps {
  symbol: string;   // e.g. 'BTC'
  height?: number;
}

declare global {
  interface Window {
    TradingView?: any;
  }
}

const TradingViewChart: React.FC<TradingViewChartProps> = ({ symbol, height = 400 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef   = useRef<any>(null);
  const scriptId    = 'tradingview-widget-script';

  const tvSymbol = TV_SYMBOL[symbol] || `BINANCE:${symbol}USDT`;

  const buildWidget = () => {
    if (!containerRef.current || !window.TradingView) return;

    // Clear old widget
    containerRef.current.innerHTML = '';

    widgetRef.current = new window.TradingView.widget({
      autosize: true,
      symbol: tvSymbol,
      interval: '60',          // 1H default
      timezone: 'Etc/UTC',
      theme: 'dark',
      style: '1',               // Candles
      locale: 'en',
      toolbar_bg: '#080808',
      enable_publishing: false,
      hide_top_toolbar: false,
      hide_legend: false,
      save_image: false,
      container_id: containerRef.current.id,
      backgroundColor: 'rgba(8, 8, 8, 0)',
      gridColor: 'rgba(255, 255, 255, 0.03)',
      overrides: {
        'paneProperties.background': '#0a0a0a',
        'paneProperties.backgroundType': 'solid',
        'scalesProperties.textColor': '#6b7280',
        'mainSeriesProperties.candleStyle.upColor':   '#00ff88',
        'mainSeriesProperties.candleStyle.downColor': '#ef4444',
        'mainSeriesProperties.candleStyle.borderUpColor':   '#00ff88',
        'mainSeriesProperties.candleStyle.borderDownColor': '#ef4444',
        'mainSeriesProperties.candleStyle.wickUpColor':   '#00ff8880',
        'mainSeriesProperties.candleStyle.wickDownColor': '#ef444480',
      },
      studies: ['Volume@tv-basicstudies'],
      withdateranges: true,
      allow_symbol_change: false,
      details: true,
      hotlist: false,
      calendar: false,
      news: [],
    });
  };

  useEffect(() => {
    // Load TradingView script once
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id   = scriptId;
      script.src  = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = buildWidget;
      document.head.appendChild(script);
    } else if (window.TradingView) {
      buildWidget();
    } else {
      // Script already loading, poll
      const interval = setInterval(() => {
        if (window.TradingView) {
          clearInterval(interval);
          buildWidget();
        }
      }, 200);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const containerId = `tv_chart_${symbol}`;

  return (
    <div
      id={containerId}
      ref={containerRef}
      className="w-full rounded-xl overflow-hidden bg-[#0a0a0a]"
      style={{ height }}
    />
  );
};

export default memo(TradingViewChart);
